import { Link } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useSocDevice, useSocTicket } from '@/api/soc';
import { HealthChip, IncidentStatusChip, SeverityChip } from '@/components/soc/SocKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { insightsDevicePath, socIncidentPath } from '@/lib/routes';

/**
 * Huntress in context: a card on PSA tickets that Huntress raised, and one on RMM devices / Device
 * Orbit. Both render nothing when Huntress isn't configured or has nothing to say.
 */

const header = (title: string, right?: React.ReactNode) => (
  <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
    {right}
  </div>
);

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-[13px] text-foreground">{children}</div>
    </div>
  );
}

const portal = (href: string) => <a href={href} target="_blank" rel="noreferrer" title="Open in Huntress" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3 w-3" /></a>;

/**
 * Huntress raises Autotask tickets titled "Huntress EDR <Severity> Incident | …" (or ITDR escalations)
 * in the Monitoring queue. Only those titles spend a lookup.
 */
export const isHuntressTicket = (title: string | null | undefined) => /^huntress\b/i.test(title?.trim() ?? '');

export function HuntressTicketCard({ ticketId, title }: { ticketId: number; title: string | null }) {
  const enabled = isHuntressTicket(title);
  const q = useSocTicket(ticketId, enabled);
  if (!enabled || q.error) return null;
  if (q.isLoading) {
    return <section className="rounded-md border border-border bg-card shadow-sm">{header('Huntress')}<div className="flex items-center gap-2 px-4 pb-3 pt-1 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding the Huntress incident…</div></section>;
  }
  const inc = q.data?.incidents[0];
  const esc = q.data?.escalations[0];
  if (!inc && !esc) return null;
  return (
    <section className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
      {header('Huntress', inc ? <Link to={socIncidentPath(inc.id)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">Incident →</Link> : null)}
      {inc && (
        <>
          <Row label="Incident"><span className="flex flex-wrap justify-end gap-1"><SeverityChip severity={inc.severity} /><IncidentStatusChip status={inc.status} /></span></Row>
          {inc.summary && <div className="border-t border-divider px-4 py-2 text-[12.5px] text-foreground">{inc.summary}</div>}
          {inc.agent ? (
            <>
              <Row label="Host">
                {inc.agent.rmm ? <Link to={insightsDevicePath(`rmm:${inc.agent.rmm.uid}`)} className="hover:underline">{inc.agent.hostname}</Link> : inc.agent.hostname}
              </Row>
              <Row label="Agent"><HealthChip a={inc.agent} /></Row>
            </>
          ) : inc.platform === 'microsoft_365' ? <Row label="Source">Microsoft 365 identity (ITDR)</Row> : null}
          <Row label="Sent"><span className="inline-flex items-center gap-1.5">{fmtRelative(inc.sentAt)}{portal(inc.portalUrl)}</span></Row>
          {inc.remediations > 0 && <Row label="Remediations">{inc.remediations}</Row>}
        </>
      )}
      {!inc && esc && (
        <>
          <Row label="Escalation"><SeverityChip severity={esc.severity} /></Row>
          <div className="border-t border-divider px-4 py-2 text-[12.5px] text-foreground">{esc.subject}</div>
          <Row label="Type">{[esc.type, esc.subtype].filter(Boolean).join(' · ')}</Row>
          <Row label="Status">{esc.resolvedAt ? `resolved ${fmtRelative(esc.resolvedAt)}` : esc.status ?? 'open'}</Row>
        </>
      )}
    </section>
  );
}

/** RMM device page / Device Orbit: the Huntress agent on this Datto device (or "none" at a Huntress client). */
export function HuntressDeviceCard({ uid }: { uid: string }) {
  const q = useSocDevice(uid);
  if (q.error || q.data?.configured === false) return null;
  if (q.isLoading) {
    return <section className="rounded-lg border border-border bg-card shadow-sm">{header('Huntress')}<div className="flex items-center gap-2 px-4 pb-3 pt-1 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking Huntress…</div></section>;
  }
  const a = q.data?.agents[0];
  if (!a) {
    return <section className="rounded-lg border border-border bg-card shadow-sm">{header('Huntress')}<div className="px-4 pb-3 pt-1 text-xs text-muted-foreground">No Huntress agent matches this device (fine unless the client has Huntress: see the gap list).</div></section>;
  }
  const incidents = q.data?.incidents ?? [];
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      {header('Huntress', portal(a.portalUrl))}
      <Row label="Agent"><HealthChip a={a} /></Row>
      <Row label="Last callback">{fmtRelative(a.lastCallback)}</Row>
      <Row label="Defender">{a.defender ?? '—'}{a.defenderDetail && <div className="text-[11px] text-muted-foreground">{a.defenderDetail}</div>}</Row>
      <Row label="Isolation">{a.isolated ? <span className="font-medium text-destructive">Isolated by Huntress</span> : 'Not isolated'}</Row>
      <Row label="Matched by">{a.rmm?.matchedBy ?? '—'}</Row>
      {incidents.length > 0 && (
        <div className="border-t border-divider px-4 py-2">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Incidents</div>
          <ul className="space-y-1">
            {incidents.slice(0, 5).map((i) => (
              <li key={i.id} className="flex items-center gap-2 text-xs">
                <SeverityChip severity={i.severity} />
                <Link to={socIncidentPath(i.id)} className="min-w-0 flex-1 truncate text-foreground hover:underline" title={i.summary ?? i.subject}>{i.summary ?? i.subject}</Link>
                <span className="shrink-0 text-muted-foreground">{fmtRelative(i.sentAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(q.data?.agents.length ?? 0) > 1 && <div className="border-t border-divider px-4 py-2 text-[11px] text-warning">{q.data!.agents.length} Huntress agents match this device (re-installed?).</div>}
    </section>
  );
}
